import { supabaseCustomRole } from "../../lib/supabase/customRoleClient.js";
import { debugLog, debugError } from '../../utils/debugLogger.js';
import { CityStats } from './cityStatsService.js';
import { fetchCachedDealerStats } from '../dealers/dealerStatsSettingsService.js';

export interface AllCitiesStats {
  city: string;
  stats: CityStats;
}

/**
 * Calculate statistics for all cities with dealers
 * Uses the get_all_cities_stats() RPC for a single SQL query instead of 4+ HTTP requests
 */
export const getAllCitiesStats = async (): Promise<AllCitiesStats[]> => {
  try {
    debugLog('general', 'Fetching all cities stats via RPC...', undefined, 'allCitiesStatsService');
    
    const { data, error } = await supabaseCustomRole.rpc('get_all_cities_stats');

    if (error) {
      debugError('general', 'RPC get_all_cities_stats error:', error, 'allCitiesStatsService');
      return [];
    }

    if (!data || data.length === 0) {
      debugLog('general', 'No cities returned from RPC', undefined, 'allCitiesStatsService');
      return [];
    }

    // Map RPC result to AllCitiesStats[] format
    const result: AllCitiesStats[] = data.map((row: any) => ({
      city: row.city,
      stats: {
        totalDealers: row.total_dealers,
        averageRating: Number(row.average_rating) || 0,
        verifiedDealers: row.verified_dealers,
        averageGoldSpread: Number(row.avg_gold_spread) || 5,
        averageSilverSpread: Number(row.avg_silver_spread) || 7,
        topRatedDealer: row.top_dealer_id ? {
          id: row.top_dealer_id,
          name: row.top_dealer_name,
          rating: Number(row.top_dealer_rating) || 0,
          city: row.city
        } : undefined
      }
    }));

    debugLog('general', `RPC returned stats for ${result.length} cities`, undefined, 'allCitiesStatsService');
    return result;
  } catch (error) {
    debugError('general', 'Error in getAllCitiesStats:', error, 'allCitiesStatsService');
    return [];
  }
};

/**
 * Get aggregated statistics for all cities (for SEO meta)
 * Uses admin_settings as the single source of truth for totalDealers and totalCities
 * to ensure consistency with dealerStats cache
 */
export const getAggregatedCitiesStats = async (precomputedStats?: AllCitiesStats[]) => {
  try {
    // Fetch admin_settings as single source of truth for aggregate counts
    const cachedStats = await fetchCachedDealerStats();
    const allStats = precomputedStats ?? await getAllCitiesStats();
    
    if (allStats.length === 0 && !cachedStats) {
      return {
        totalCities: 0,
        totalDealers: 0,
        averageRating: 0,
        averageGoldSpread: 5,
        averageSilverSpread: 7
      };
    }

    // Use admin_settings counts as priority (single source of truth)
    const totalCities = cachedStats?.totalCities || allStats.length;
    const totalDealers = cachedStats?.totalDealers || allStats.reduce((sum, city) => sum + city.stats.totalDealers, 0);
    
    // Rating and spread are always calculated live from city data
    const totalRatings = allStats.filter(city => city.stats.averageRating > 0);
    const averageRating = totalRatings.length > 0
      ? totalRatings.reduce((sum, city) => sum + city.stats.averageRating, 0) / totalRatings.length
      : 4.0;
    const averageGoldSpread = allStats.length > 0 
      ? allStats.reduce((sum, city) => sum + city.stats.averageGoldSpread, 0) / allStats.length
      : 5;
    const averageSilverSpread = allStats.length > 0
      ? allStats.reduce((sum, city) => sum + city.stats.averageSilverSpread, 0) / allStats.length
      : 7;

    debugLog('general', `Aggregated stats: totalDealers=${totalDealers} (from ${cachedStats ? 'admin_settings' : 'live'}), totalCities=${totalCities}`, undefined, 'allCitiesStatsService');

    return {
      totalCities,
      totalDealers,
      averageRating: Math.round(averageRating * 10) / 10,
      averageGoldSpread: Math.round(averageGoldSpread * 10) / 10,
      averageSilverSpread: Math.round(averageSilverSpread * 10) / 10
    };
  } catch (error) {
    debugError('general', 'Error calculating aggregated stats:', error, 'allCitiesStatsService');
    return {
      totalCities: 0,
      totalDealers: 0,
      averageRating: 0,
      averageGoldSpread: 5,
      averageSilverSpread: 7
    };
  }
};
